import { supabase } from "@/integrations/supabase/client";

export interface KolData {
  id: string;
  creator_name: string;
  social_media_type: string;
  social_media_handler: string;
  social_media_name: string;
  payment_structure: string;
  currency_type: string;
  region: string;
  reward_station_url: string;
  reward_station_type: string;
  metamorphosis_referral_code: string;
  kol_shortcut: string;
  kol_type: string;
  internal_kol_type?: string;
  tier: string;
  start_date: string | null;
  end_date: string | null;
  pic_id: string[] | null | any;
  content_type?: string[] | null;
  price_content_type?: Record<string, number> | null;
  profile_image_url?: string;
  created_at?: string;
  updated_at?: string;
  parent_id?: string | null;
}

export interface HierarchicalKolData extends KolData {
  children?: HierarchicalKolData[];
  isExpanded?: boolean;
}

export const processKolData = (data: KolData[]): HierarchicalKolData[] => {
  const processed = data.map(item => ({
    ...item,
    start_date: item.start_date ? item.start_date : null,
    end_date: item.end_date ? item.end_date : null,
    content_type: Array.isArray(item.content_type) 
      ? item.content_type.map(ct => typeof ct === 'string' ? ct : String(ct))
      : [],
    pic_id: Array.isArray(item.pic_id) ? item.pic_id : [],
    created_at: item.created_at || null,
    updated_at: item.updated_at || null,
  }));

  // Build hierarchical structure - only root items (no parent_id) should be at top level
  const hierarchical = buildHierarchy(processed);
  
  // Sort by created_at descending (latest first)
  return hierarchical.sort((a, b) => {
    const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
    const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
    return dateB - dateA;
  });
};

const buildHierarchy = (data: HierarchicalKolData[]): HierarchicalKolData[] => {
  const itemMap = new Map<string, HierarchicalKolData>();
  const rootItems: HierarchicalKolData[] = [];

  // First pass: create map of all items
  data.forEach(item => {
    itemMap.set(item.id, { ...item, children: [], isExpanded: false });
  });

  // Second pass: build parent-child relationships
  data.forEach(item => {
    const processedItem = itemMap.get(item.id)!;
    
    if (item.parent_id && itemMap.has(item.parent_id)) {
      // This is a child item - add it to parent's children
      const parent = itemMap.get(item.parent_id)!;
      parent.children!.push(processedItem);
    } else if (!item.parent_id) {
      // This is a root item (no parent_id) - add to root items
      rootItems.push(processedItem);
    }
    // Items with parent_id that don't have a matching parent are ignored
    // (they won't appear as root items)
  });

  return rootItems;
};

export const fetchPicOptions = async () => {
  try {
    const { data, error } = await supabase
      .from('kol_pic')
      .select('name')
      .order('name');
    
    if (error) throw error;
    
    return data ? data.map(pic => ({
      value: pic.name,
      label: pic.name
    })) : [];
  } catch (err) {
    console.error('Error fetching PIC options:', err);
    return [];
  }
};

export const processRowUpdate = async (newRow: any, oldRow: any, onDataChange?: () => void) => {
  const formattedStartDate = newRow.start_date instanceof Date 
    ? newRow.start_date.toISOString().split('T')[0] 
    : newRow.start_date;
    
  const formattedEndDate = newRow.end_date instanceof Date 
    ? newRow.end_date.toISOString().split('T')[0] 
    : newRow.end_date;

  const contentType = Array.isArray(newRow.content_type) 
    ? newRow.content_type 
    : (newRow.content_type ? [newRow.content_type] : []);
  
  const picId = Array.isArray(newRow.pic_id) 
    ? newRow.pic_id 
    : (newRow.pic_id ? [newRow.pic_id] : []);
  
  const { error } = await supabase
    .from('kol_db')
    .update({
      creator_name: newRow.creator_name,
      social_media_type: newRow.social_media_type,
      social_media_handler: newRow.social_media_handler,
      social_media_name: newRow.social_media_name,
      payment_structure: newRow.payment_structure,
      currency_type: newRow.currency_type,
      region: newRow.region,
      tier: newRow.tier,
      kol_type: newRow.kol_type,
      internal_kol_type: newRow.internal_kol_type,
      content_type: contentType,
      pic_id: picId,
      start_date: formattedStartDate,
      end_date: formattedEndDate,
    })
    .eq('id', newRow.id);

  if (error) throw error;
  
  if (onDataChange) onDataChange();
  
  return {
    ...newRow,
    content_type: contentType,
    pic_id: picId
  };
};
